import {
  IconButton,
  List,
  ListItem,
  Popper,
  PopperPlacementType,
  styled,
} from '@mui/material'
import { ColDef, ColumnState } from 'ag-grid-community'
import { FontSize } from '../../../../../styles/commonStyles'
import CloseIcon from '@mui/icons-material/Close'
import { colorPalette } from '../../../../style/colorPalette'
import { Button } from '../../../../components/buttons'
import { intl } from '../../../../../i18n'
import AscIcon from '../../../../components/icons/AscIcon'
import DescIcon from '../../../../components/icons/DescIcon'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'
import { useCallback } from 'react'

type Props = {
  open: boolean
  anchorEl: HTMLButtonElement | null
  placement?: PopperPlacementType
  onDeleteSortedColumn: ((column: string | ColDef<any>) => void) | undefined
  onDeleteSortedAllColumns: (() => void) | undefined
  onChangeSortColumnState?:
    | ((column: string | ColDef<any>, sort: 'asc' | 'desc' | null) => void)
    | undefined
  sortColumnsState: SortedColumnState[]
  getDisplayColumnName?: (colDef: ColDef) => string | undefined
}

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
  background: colorPalette.monotone[0],
  borderRadius: '4px',
  boxShadow: '0px 1px 2px #0000004D',
}))

const StyledList = styled(List)(() => ({
  position: 'relative',
  overflow: 'auto',
  width: 'fit-content',
  padding: '10px',
  maxHeight: '430px',
}))

const StyledSortCriteria = styled('span')(() => ({
  fontSize: FontSize.MEDIUM,
  fontWeight: 400,
  textAlign: 'center',
  display: 'block',
}))

const StyledDeleteButton = styled(Button)(() => ({
  border: 'none',
  background: 'transparent',
  padding: '15px 15px 15px 15px',
  cursor: 'pointer',
  width: '100%',
}))

const StyledSortDelete = styled('span')(() => ({
  color: colorPalette.pink[6],
  fontSize: FontSize.MEDIUM,
  fontWeight: 400,
}))

const StyledListItem = styled(ListItem)(() => ({
  padding: '0px',
  background: colorPalette.monotone[1],
  paddingRight: '8px',
  paddingLeft: '8px',
  border: 'none',
  borderRadius: '32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '5px 0 10px 0',
}))

const StyledDisplayColumnButton = styled(Button)(() => ({
  width: 'fit-content',
  minWidth: '120px',
  height: '30px',
  border: 'none',
  color: colorPalette.monotone[4],
  fontSize: FontSize.MEDIUM,
  background: 'transparent',
}))

const StyledIconButton = styled(IconButton)(() => ({
  padding: '3px',
  background: colorPalette.monotone[0],
}))

const StyledCloseIcon = styled(CloseIcon)(() => ({
  height: '10px',
  width: '10px',
}))

const SortColumnListPopper = ({
  open,
  anchorEl,
  placement,
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  sortColumnsState,
  getDisplayColumnName,
}: Props) => {
  const getDisplayValue = useCallback(
    (column: ColDef | string): string => {
      if (typeof column === 'string') return column

      const colDef: ColDef = column
      const value = getDisplayColumnName
        ? getDisplayColumnName(colDef)
        : undefined
      return value ?? colDef.headerName ?? ''
    },
    [getDisplayColumnName]
  )
  return (
    <StyledPopper
      open={open}
      anchorEl={anchorEl}
      placement={placement || 'right-start'}
      disablePortal={true}
    >
      <StyledList as="nav">
        <StyledSortCriteria>
          {intl.formatMessage({ id: 'wbs.header.sort.criteria' })}
        </StyledSortCriteria>
        <StyledDeleteButton
          colorPattern="monotone"
          onClick={onDeleteSortedAllColumns}
        >
          <StyledSortDelete>
            {intl.formatMessage({ id: 'wbs.header.sort.delete' })}
          </StyledSortDelete>
        </StyledDeleteButton>
        {sortColumnsState?.map((column: SortedColumnState, index) => {
          return (
            <StyledListItem
              key={`filteredColumn${index}`}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              {sortColumnsState &&
                (sortColumnsState[index].sort === 'asc' ? (
                  <AscIcon />
                ) : (
                  <DescIcon />
                ))}
              <StyledDisplayColumnButton
                key={`filteredColumn${index}`}
                onClick={
                  onChangeSortColumnState
                    ? () => {
                        onChangeSortColumnState(
                          typeof column === 'string'
                            ? column
                            : column.colId || column.field || '',
                          sortColumnsState &&
                            sortColumnsState[index].sort === 'asc'
                            ? 'desc'
                            : 'asc'
                        )
                      }
                    : undefined
                }
                colorPattern="monotone"
              >
                {getDisplayValue(column)}
              </StyledDisplayColumnButton>
              {onDeleteSortedColumn && (
                <StyledIconButton
                  onClick={() =>
                    onDeleteSortedColumn(
                      typeof column === 'string'
                        ? column
                        : column.colId || column.field || ''
                    )
                  }
                >
                  <StyledCloseIcon fontSize="small" />
                </StyledIconButton>
              )}
            </StyledListItem>
          )
        })}
      </StyledList>
    </StyledPopper>
  )
}

export default SortColumnListPopper
